import { snakeize } from 'casing'
import { AnyAction, Dispatch } from 'redux'
import { AxiosError } from 'axios'
import {
  LoadingTypes,
  RequestFailureMessage,
  errorLogger,
  getErrorMessages,
  startLoading,
  stopLoading,
  updateAppAlert,
  type CloudType,
  type ProductType,
} from '@labrav/common'
import type { AccountDataSelection } from '@labrav/common'
import type { RootState } from '../../store/types'
import {
  createCloudAccount,
  deleteCloudAccountByCloudAndProduct,
  getCloudAccounts,
} from './accountSelectionApi'

const AccountSelectionActionTypes = {
  CREATE_ACCOUNT_LOADING: 'CREATE_ACCOUNT_LOADING',
  CREATE_ACCOUNT_SUCCESS: 'CREATE_ACCOUNT_SUCCESS',
  CREATE_ACCOUNT_ERROR: 'CREATE_ACCOUNT_ERROR',
  DELETE_ACCOUNT_LOADING: 'DELETE_ACCOUNT_LOADING',
  DELETE_ACCOUNT_SUCCESS: 'DELETE_ACCOUNT_SUCCESS',
  DELETE_ACCOUNT_ERROR: 'DELETE_ACCOUNT_ERROR',
  SET_AVAILABLE_ACCOUNTS: 'SET_AVAILABLE_ACCOUNTS',
  ACCOUNT_SELECTION_LOADING: 'ACCOUNT_SELECTION_LOADING',
  ACCOUNT_SELECTION_ERROR: 'ACCOUNT_SELECTION_ERROR',
} as const

const setAvailableAccounts = (accounts: AccountDataSelection[]) => ({
  type: AccountSelectionActionTypes.SET_AVAILABLE_ACCOUNTS,
  payload: { accounts: Array.isArray(accounts) ? accounts : [] },
})

export const loadAvailableAccounts =
  (partnerId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.LOAD_AVAILABLE_ACCOUNTS))
    dispatch({ type: AccountSelectionActionTypes.ACCOUNT_SELECTION_LOADING })
    try {
      const { data } = await getCloudAccounts(partnerId)
      const accounts = Array.isArray(data) ? data : []
      dispatch(setAvailableAccounts(accounts))
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )
      dispatch({
        type: AccountSelectionActionTypes.ACCOUNT_SELECTION_ERROR,
        payload: errMess,
      })
      errorLogger({ globalState: getState() })(err as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.LOAD_AVAILABLE_ACCOUNTS))
    }
  }

export const createAccountAction =
  (partnerId: string, payload: Partial<AccountDataSelection>) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({ type: AccountSelectionActionTypes.CREATE_ACCOUNT_LOADING })

    try {
      const { data } = await createCloudAccount(
        snakeize(payload) as Partial<AccountDataSelection>
      )

      dispatch({
        type: AccountSelectionActionTypes.CREATE_ACCOUNT_SUCCESS,
        payload: data,
      })

      await dispatch(loadAvailableAccounts(partnerId) as unknown as AnyAction)

      dispatch(
        updateAppAlert({
          message: 'Account created successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.CREATE_ACCOUNT_ERROR,
        payload: errMess,
      })

      errorLogger({ globalState: getState() })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const deleteAccountAction =
  (accountId: string, cloudType: CloudType, productType: ProductType) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    dispatch({ type: AccountSelectionActionTypes.DELETE_ACCOUNT_LOADING })

    try {
      await deleteCloudAccountByCloudAndProduct(
        accountId,
        cloudType,
        productType
      )

      dispatch({
        type: AccountSelectionActionTypes.DELETE_ACCOUNT_SUCCESS,
        payload: accountId,
      })

      const state = getState()
      const currentAccount = (
        state.accountSelection?.availableAccounts as
          | AccountDataSelection[]
          | undefined
      )?.find(account => account.id === accountId)

      if (currentAccount?.partnerId) {
        await dispatch(
          loadAvailableAccounts(
            currentAccount.partnerId
          ) as unknown as AnyAction
        )
      }

      dispatch(
        updateAppAlert({
          message: 'Account deleted successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )

      dispatch({
        type: AccountSelectionActionTypes.DELETE_ACCOUNT_ERROR,
        payload: errMess,
      })

      errorLogger({ globalState: getState() })(err as Error)

      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
